import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { fmtDate, fmtMoney, todayStr, daysBetween } from '../lib/format.js';
import { CATEGORY_ICON, CATEGORY_LABEL } from '../lib/constants.js';
import { DocumentUploadModal } from './DocumentUploadModal.jsx';
import { docIcon } from '../lib/ui.js';

// ─── ComplianceReminderDetailModal ────────────────────────────────────────────
export function ComplianceReminderDetailModal({ reminder, user, users, location, onClose, onComplete, onEdit, onDelete, onDocUploaded, isOwner }) {
  const [linkedDocs, setLinkedDocs] = React.useState([]);
  const [showUpload, setShowUpload] = React.useState(false);
  const [completing, setCompleting] = React.useState(false);

  React.useEffect(() => {
    api('GET', `/api/compliance/documents?reminder_id=${reminder.id}`)
      .then(setLinkedDocs).catch(() => {});
  }, [reminder.id]);

  const today = todayStr();
  const daysUntil = daysBetween(today, reminder.due_date);
  const isOverdue = daysUntil < 0;

  let lease = null;
  try { lease = reminder.lease_details ? JSON.parse(reminder.lease_details) : null; } catch {}

  async function handleComplete() {
    if (!confirm('Mark this reminder as completed?')) return;
    setCompleting(true);
    try {
      const result = await api('POST', `/api/compliance/reminders/${reminder.id}/complete`, { completed_by: user.id });
      onComplete(result.next_due);
    } catch(e) { alert(e.message); }
    finally { setCompleting(false); }
  }

  async function handleDownload(doc) {
    try {
      const result = await api('GET', `/api/compliance/documents/${doc.id}/download`);
      const link = document.createElement('a');
      link.href = result.file_data;
      link.download = doc.name;
      link.click();
    } catch(e) { alert('Download failed: ' + e.message); }
  }

  async function handleDeleteDoc(docId) {
    if (!confirm('Delete this document?')) return;
    await api('DELETE', `/api/compliance/documents/${docId}`);
    setLinkedDocs(d => d.filter(x => x.id !== docId));
  }

  const dueBadge = isOverdue
    ? <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-bold">{Math.abs(daysUntil)}d overdue</span>
    : daysUntil <= 30
    ? <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-bold">{daysUntil}d left</span>
    : <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-bold">{daysUntil}d away</span>;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-cream w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl max-h-[92vh] flex flex-col fade-in shadow-2xl" onClick={e => e.stopPropagation()}>
        {showUpload && (
          <DocumentUploadModal reminderId={reminder.id} location={location} user={user}
            onClose={() => setShowUpload(false)}
            onSave={() => {
              setShowUpload(false);
              api('GET', `/api/compliance/documents?reminder_id=${reminder.id}`).then(setLinkedDocs);
              onDocUploaded && onDocUploaded();
            }} />
        )}
        <div className="bg-ink rounded-t-2xl p-5 flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl">{CATEGORY_ICON[reminder.category] || '📌'}</span>
                <span className="text-cream/50 text-xs uppercase tracking-wider font-semibold">{CATEGORY_LABEL[reminder.category] || 'Other'}</span>
                {dueBadge}
              </div>
              <h3 className="text-cream font-bold text-xl">{reminder.title}</h3>
              <p className="text-cream/50 text-sm mt-0.5">Due {fmtDate(reminder.due_date)} · {reminder.recurrence}</p>
            </div>
            <button onClick={onClose} className="text-cream/40 hover:text-cream text-2xl leading-none mt-1">×</button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-5">
          {/* Lease summary panel */}
          {lease && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-3">🏢 Lease Summary</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">Monthly Rent</span><p className="font-bold text-ink">{fmtMoney(lease.monthly_rent)}</p></div>
                <div><span className="text-gray-500">Next Year (est.)</span>
                  <p className="font-bold text-ink">
                    {lease.monthly_rent && lease.annual_increase != null
                      ? fmtMoney(Math.round(lease.monthly_rent * (1 + lease.annual_increase / 100)))
                      : '—'}
                  </p>
                </div>
                <div><span className="text-gray-500">Days Until Renewal</span>
                  <p className={`font-bold ${isOverdue ? 'text-red-600' : daysUntil <= 90 ? 'text-amber-600' : 'text-green-600'}`}>
                    {isOverdue ? `${Math.abs(daysUntil)}d overdue` : `${daysUntil} days`}
                  </p>
                </div>
                <div><span className="text-gray-500">Notice Deadline</span>
                  <p className="font-bold text-ink">
                    {lease.lease_end && lease.notice_period
                      ? fmtDate(new Date(new Date(lease.lease_end + 'T12:00:00Z') - lease.notice_period * 86400000).toISOString().slice(0, 10))
                      : '—'}
                  </p>
                </div>
                {lease.landlord_name && <div><span className="text-gray-500">Landlord</span><p className="font-semibold text-ink">{lease.landlord_name}</p></div>}
                {lease.landlord_contact && <div><span className="text-gray-500">Contact</span><p className="font-semibold text-ink">{lease.landlord_contact}</p></div>}
                {lease.annual_increase != null && <div><span className="text-gray-500">Annual Increase</span><p className="font-semibold text-ink">{lease.annual_increase}%</p></div>}
                {lease.notice_period && <div><span className="text-gray-500">Notice Required</span><p className="font-semibold text-ink">{lease.notice_period} days</p></div>}
              </div>
            </div>
          )}

          {reminder.description && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Description</p>
              <p className="text-sm text-gray-700">{reminder.description}</p>
            </div>
          )}

          {reminder.guide_text && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-2">📋 Instructions</p>
              <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{reminder.guide_text}</p>
            </div>
          )}

          {/* Linked Documents */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Documents ({linkedDocs.length})</p>
              <button onClick={() => setShowUpload(true)}
                className="text-xs font-semibold text-crimson hover:text-crimson-dark">+ Upload</button>
            </div>
            {linkedDocs.length === 0 ? (
              <p className="text-sm text-gray-400 py-2">No documents linked yet.</p>
            ) : (
              <div className="space-y-2">
                {linkedDocs.map(doc => (
                  <div key={doc.id} className="flex items-center gap-3 bg-white border border-gray-100 rounded-lg px-3 py-2">
                    <span className="text-lg flex-shrink-0">{docIcon(doc.file_type)}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-ink truncate">{doc.name}</p>
                      <p className="text-xs text-gray-400">
                        {doc.category} · {new Date(doc.uploaded_at).toLocaleDateString()}
                        {doc.expiration_date && ` · Expires ${fmtDate(doc.expiration_date)}`}
                      </p>
                    </div>
                    <button onClick={() => handleDownload(doc)}
                      className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex-shrink-0">⬇ Download</button>
                    {isOwner && (
                      <button onClick={() => handleDeleteDoc(doc.id)}
                        className="text-xs text-red-400 hover:text-red-600 flex-shrink-0 ml-1">✕</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="p-5 border-t border-gray-200 flex gap-2 flex-shrink-0 flex-wrap">
          {isOwner && (
            <>
              <button onClick={() => onEdit(reminder)}
                className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">Edit</button>
              <button onClick={() => { if (confirm('Delete this reminder?')) onDelete(reminder.id); }}
                className="px-4 py-2 border border-red-200 text-red-500 rounded-xl text-sm font-medium hover:bg-red-50">Delete</button>
            </>
          )}
          <div className="flex-1" />
          {reminder.status === 'active' && (
            <button onClick={handleComplete} disabled={completing}
              className="px-5 py-2 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 disabled:opacity-50">
              {completing ? 'Marking...' : '✓ Mark Complete'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
